import { FilterAlt } from "@mui/icons-material";
import { styled, useTheme } from '@mui/material/styles';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Divider, Drawer, Grid, IconButton, Paper, Toolbar, Typography, FormControl, Select, MenuItem } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from "react";
import AppPagination from "../../app/components/AppPagination";
import { CheckboxButtons, ComplexCheckboxButtons } from "../../app/components/CheckboxButtons";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import BookList from "./BookList";
import BookSearch from "./BookSearch";
import { bookSelectors, fetchBooksAsync, fetchFiltersAsync, setBookParams, setPageNumber } from "./catalogSlice";
import React from "react";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CssBaseline from '@mui/material/CssBaseline';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';


const drawerWidth = 360;

// Responsive Main styled component using width state
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'windowWidth' })<{
  open?: boolean;
  windowWidth: number;
}>(({ theme, open, windowWidth }) => ({
  flexGrow: 1,
  padding: theme.spacing(1),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: windowWidth <= 599 ? 0 : `-${drawerWidth}px`,

  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
}));


const Catalog: React.FC = () => {
  const books = useAppSelector(bookSelectors.selectAll);
  const {
    initialLoad,
    booksLoaded,
    filtersLoaded,
    bookParams,
    bookFormats,
    bookTypes,
    genres,
    languages,
    targetAudiences,
    countries,
    yearsOfPublication,
    metaData
  } = useAppSelector(state => state.catalog);
  const dispatch = useAppDispatch();
  const [formatsSelected, setFormatsSelected] = useState<string[]>(bookParams.bookFormats);
  const [typesSelected, setTypesSelected] = useState<string[]>(bookParams.bookTypes);
  const [genresSelected, setGenresSelected] = useState<string[]>(bookParams.genres);
  const [languagesSelected, setLanguagesSelected] = useState<string[]>(bookParams.languages);
  const [targetAudiencesSelected, setTargetAudiencesSelected] = useState<string[]>(bookParams.targetAudiences);
  const [countriesSelected, setCountriesSelected] = useState<string[]>(bookParams.countries);
  const [yearsOfPublicationSelected, setYearsOfPublicationSelected] = useState<string[]>(bookParams.yearsOfPublication);
  const theme = useTheme();
  const [open, setOpen] = useState(window.innerWidth > 500);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    if (!booksLoaded) {
      window.scrollTo(0, 0);
      dispatch(fetchBooksAsync());
    }
  }, [booksLoaded, dispatch]);

  useEffect(() => {
    if (!filtersLoaded) {
      dispatch(fetchFiltersAsync());
    }
  }, [filtersLoaded, dispatch]);


  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth <= 990) {
        setOpen(false);
      } else {
        setOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    // Initial check
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!filtersLoaded || initialLoad) return <Box sx={{ height: 500 }}><LoadingComponent message='Loading books...' /></Box>

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
    position: 'static'
  }));

  const bookTypeLabels: Record<string, string> = {
    Fiction: 'Fiction',
    Nonfiction: 'Non-Fiction',
    ChildrensBook: "Children's Book"
  };

  const mapBookTypes = bookTypes.map(type => ({
    label: bookTypeLabels[type] || type,
    value: type
  }));


  const getBookTypeValueMap = Object.fromEntries(
    Object.entries(bookTypeLabels).map(([value, label]) => [label, value])
  );

  return (
    <Box sx={{ p: 1, mb: 5, display: 'flex', width: '100%' }} >
      <Drawer
        sx={{
          width: {
            xs: '100%',
            sm: drawerWidth,
          },
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: {
              xs: '100%',
              sm: drawerWidth,
            },
            boxSizing: 'border-box',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
            zIndex: 1,
            borderRight: 'none',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
        className="catalogMainDrawer"
        style={window.innerWidth <= 599 ? { display: open ? 'block' : 'none' } : undefined}
      >
        <DrawerHeader>
          <IconButton onClick={() => setOpen(false)} sx={{ mt: 5 }} component="a" {...{ name: "close-drawer-icon" }} id="close-drawer-btn">
            {theme.direction === 'ltr' ? <CloseOutlinedIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        {/* filter here */}
        <Paper sx={{ mb: 2, p: 2, border: "none", boxShadow: "none" }} className="catalogDrawer">


          <Accordion sx={{ mt: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              {/* Book Type before */}
              <Typography variant='body1'>Genre</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CheckboxButtons
                items={mapBookTypes.map(type => type.label)}
                checked={bookParams.bookTypes}
                disabled={!booksLoaded}
                rowOrientation={true}
                onChange={(items: string[]) => {
                  const checkedBookTypeValues = items.map(label => getBookTypeValueMap[label] || label);

                  setTimeout(() => {
                    setTypesSelected(checkedBookTypeValues);
                  }, 200);

                }}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion sx={{ mt: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              {/* Genre before */}
              <Typography variant='body1'>Category</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ComplexCheckboxButtons
                items={genres.filter(g => g !== "Children's Book")}
                checked={bookParams.genres}
                disabled={!booksLoaded}
                rowOrientation={true}
                onChange={(items: string[]) => setGenresSelected(items)}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion sx={{ mt: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography variant='body1'>Language</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ComplexCheckboxButtons
                items={languages}
                checked={bookParams.languages}
                disabled={!booksLoaded}
                rowOrientation={true}
                onChange={(items: string[]) => setLanguagesSelected(items)}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion sx={{ mt: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography variant='body1'>Target Audience</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ComplexCheckboxButtons
                items={targetAudiences}
                checked={bookParams.targetAudiences}
                disabled={!booksLoaded}
                rowOrientation={true}
                onChange={(items: string[]) => setTargetAudiencesSelected(items)}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion sx={{ mt: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography variant='body1'>Country</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CheckboxButtons
                items={countries}
                checked={bookParams.countries}
                disabled={!booksLoaded}
                rowOrientation={true}
                onChange={(items: string[]) => setCountriesSelected(items)}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion sx={{ mt: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography variant='body1'>Book Format</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CheckboxButtons
                items={bookFormats}
                checked={bookParams.bookFormats}
                disabled={!booksLoaded}
                rowOrientation={true}
                onChange={(items: string[]) => setFormatsSelected(items)}
              />
            </AccordionDetails>
          </Accordion>
          <Box sx={{ mt: 2, mb: 2 }}>
            <FormControl fullWidth size="small" disabled={!booksLoaded}>
              <Select
                name="publicationYear"
                id="publication-year-select"
                value={yearsOfPublicationSelected[0] || ''}
                onChange={e => setYearsOfPublicationSelected(e.target.value ? [e.target.value] : [])}
                displayEmpty
              >
                <MenuItem value="">Publication Year</MenuItem>
                {yearsOfPublication.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Divider sx={{ mt: 2 }} />
          <Button
            variant='contained'
            startIcon={<FilterAlt />}
            size='small'
            onClick={() => dispatch(setBookParams({
              bookFormats: formatsSelected,
              bookTypes: typesSelected,
              genres: genresSelected,
              languages: languagesSelected,
              targetAudiences: targetAudiencesSelected,
              countries: countriesSelected,
              yearsOfPublication: yearsOfPublicationSelected
            }))}
            sx={{ mt: 2, color: 'white' }}
            disabled={!booksLoaded}
            fullWidth>
            Filter
          </Button>
        </Paper>

      </Drawer>


  <Main open={open} windowWidth={windowWidth}>

        <Grid justifyContent="center">

          <Grid size={{ xs: 12, lg: 12, sm: 12 }}>
            <Paper sx={{ mb: 2, p: 0, border: "none", boxShadow: "none" }}>

              <Grid container spacing={2} sx={{ mb: 3, mt: 2 }}>
                <Grid rowSpacing={1} size={{ md: 6, sm: 6, xs: 4 }}>
                  <CssBaseline />
                  {!open && (
                    <Toolbar>
                      {/* Only show the IconButton if the drawer is closed (open === false) */}

                      <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={() => setOpen(true)}
                        edge="start"
                        component="a" {...{ name: "open-drawer-icon" }} id="open-drawer-btn"
                      >
                        <FilterAltOutlinedIcon />
                        <Typography variant="overline" sx={{ mt: 1 }}>Filter</Typography>
                      </IconButton>

                    </Toolbar>
                  )}
                </Grid>
                <Grid rowSpacing={1} size={{ md: 6, sm: 6, xs: 8 }}>
                  <Grid sx={{float: 'right' }} className="bookSearchGrid">
                    <BookSearch disabled={!booksLoaded} />
                  </Grid>
                </Grid>
              </Grid>
              <Paper sx={{ pb: 2, border: "none", boxShadow: "none" }} >
                <BookList books={books} />
              </Paper>
              <Divider sx={{ mt: 2, mb: 2 }} />
              {metaData &&
                <AppPagination
                  metaData={metaData}
                  onPageChange={(page: number) => dispatch(setPageNumber({ pageNumber: page }))}
                />
              }
            </Paper>
          </Grid>
        </Grid>
      </Main>
    </Box>
  )
}

export default Catalog;
